import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CataxLogo from '../assets/CataxLogo';
import {
  ConsentParams,
  parseConsentParams,
  submitConsent,
  SCOPE_LABELS,
} from '../services/OAuthConsentService';

/**
 * OAuth 2.0 consent screen for MCP clients (Claude Code, etc.).
 *
 * Reached when the backend `/api/v1/oauth/authorize` endpoint redirects the
 * browser here. Public route: if the user is not signed in we send them through
 * the normal login flow and return here afterwards (via `post_login_redirect`).
 */
const OAuthConsentPage: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Capture params once — they live only in the initial URL.
  const params: ConsentParams | null = useMemo(
    () => parseConsentParams(location.search),
    [location.search],
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  // Not signed in → stash this URL and route through login, then come back.
  useEffect(() => {
    if (!currentUser) {
      sessionStorage.setItem('post_login_redirect', location.pathname + location.search);
      navigate('/login', { replace: true });
    }
  }, [currentUser, location.pathname, location.search, navigate]);

  const scopes = useMemo(
    () => (params?.scope ? params.scope.split(' ').filter(Boolean) : []),
    [params],
  );

  const handleDecision = async (approved: boolean) => {
    if (!params) return;
    setError('');
    setSubmitting(true);
    try {
      const redirectTo = await submitConsent(params, approved);
      // Hand control back to the MCP client's callback URL.
      window.location.href = redirectTo;
    } catch (e: any) {
      console.error('Consent submission failed:', e);
      setError(
        e?.response?.data?.detail ||
          'Could not complete the authorization. Please try again.',
      );
      setSubmitting(false);
    }
  };

  if (!currentUser) {
    return null; // redirecting to /login
  }

  if (!params) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid authorization request</h1>
          <p className="text-gray-500 text-sm">
            This link is missing required parameters. Please start the connection
            again from your MCP client.
          </p>
        </div>
      </div>
    );
  }

  const clientName = params.client_name || params.client_id;
  const userLabel = currentUser.email || (currentUser as any).name || currentUser.uid;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="text-center mb-6">
          <CataxLogo className="w-32 h-auto mx-auto mb-6" />
          <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            Authorize <span className="font-bold">{clientName}</span>
          </h1>
          <p className="text-gray-500 text-sm mt-2">
            wants to access Cata on your behalf as{' '}
            <span className="font-medium text-gray-700">{userLabel}</span>
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            This will allow it to
          </p>
          <ul className="space-y-2.5">
            {scopes.map((s) => (
              <li key={s} className="flex items-start gap-2.5">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{SCOPE_LABELS[s] || s}</span>
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => handleDecision(false)}
            disabled={submitting}
            className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition-all"
          >
            Deny
          </button>
          <button
            onClick={() => handleDecision(true)}
            disabled={submitting}
            className="flex-1 py-3 px-4 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 disabled:opacity-50 transition-all"
          >
            {submitting ? 'Authorizing…' : 'Authorize'}
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          You can revoke this access at any time from your account settings.
        </p>
      </div>
    </div>
  );
};

export default OAuthConsentPage;
